import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, getDocs, where, doc, getDoc } from '../firebaseConfig';
import { Scatter } from 'react-chartjs-2';
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import Divider from "@mui/material/Divider";
import TimelineIcon from '@mui/icons-material/Timeline';
import MDTypography from "components/MDTypography";
import { MenuItem, Select } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

const RRRatioAnalysisChart = ({ userID, tradeAccountID = 'default' }) => {
  const [trades, setTrades] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [accountName, setAccountName] = useState(tradeAccountID); // Default to ID
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

      // Fetch account name on mount
      useEffect(() => {
        const fetchAccountName = async () => {
          if (tradeAccountID === 'default') {
            setAccountName('Default');
            return;
          }
          try {
            const accountDoc = await getDoc(doc(db, 'Users', userID, 'tradeAccounts', tradeAccountID));
            if (accountDoc.exists()) {
              setAccountName(accountDoc.data().name || tradeAccountID);
            }
          } catch (error) {
            console.error('Error fetching account name:', error);
          }
        };
        fetchAccountName();
      }, [userID, tradeAccountID]);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const tradesQuery = query(
          collection(db, tradesPath),
          where('status', '==', 'Closed')
        );
        const tradeSnapshot = await getDocs(tradesQuery);
        const tradesData = tradeSnapshot.docs.map(doc => ({
          id: doc.id,
          accountID: tradeAccountID,
          ...doc.data()
        }));
        setTrades(tradesData);
      } catch (error) {
        console.error('Error fetching trades for RRRatioAnalysisChart:', error);
      }
    };
    fetchTrades();
  }, [userID, tradeAccountID]);

  const calculateRRData = () => {
    return trades
      .filter(trade => new Date(trade.exitDate).getFullYear() === year)
      .map(trade => {
        const risk = trade.entry - trade.sl;
        const reward = trade.tp - trade.entry;
        const rrRatio = risk !== 0 ? reward / risk : 0;
        const outcome = trade.returns > 0 ? 'Win' : 'Loss';
        const returns = parseFloat(trade.returns || 0);

        if (rrRatio < -100 || rrRatio > 100 || returns < -10000 || returns > 10000) return null;

        return { x: returns, y: rrRatio, outcome };
      })
      .filter(trade => trade !== null);
  };

  const data = {
    datasets: [
      {
        label: 'Wins',
        data: calculateRRData().filter(d => d.outcome === 'Win'),
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        pointRadius: 6,
        pointHoverRadius: 8,
      },
      {
        label: 'Losses',
        data: calculateRRData().filter(d => d.outcome === 'Loss'),
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: { title: { display: true, text: 'Returns ($)' }, type: 'linear', grid: { display: true } },
      y: { title: { display: true, text: 'Risk-Reward Ratio' }, type: 'linear', grid: { display: true }, ticks: { stepSize: 1 } },
    },
    plugins: {
      tooltip: { callbacks: { label: (context) => `Return: $${context.raw.x.toFixed(2)}, RR: ${context.raw.y.toFixed(2)}` } },
    },
  };

  const handleYearChange = (event) => setYear(event.target.value);

  const tooltipContent = `This chart shows how your risk-reward (RR) ratios correlate with trade outcomes. Each point represents a trade, with the Y-axis showing the RR ratio (TP/SL) and the X-axis showing returns. Green points are wins, red are losses.`;

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox variant="gradient" bgColor="light" color="dark" coloredShadow="light" borderRadius="xl" display="flex" justifyContent="center" alignItems="center" width="4rem" height="4rem" mt={-3}>
            <TimelineIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Risk-Reward Ratio Analysis {tradeAccountID === 'default' ? '' : `(${accountName})`}
            </MDTypography>
            <Tooltip title={tooltipContent} placement="bottom-end" componentsProps={{ tooltip: { sx: { bgcolor: theme.palette.background.paper, color: theme.palette.text.primary, border: `1px solid ${theme.palette.divider}`, p: 1.5, maxWidth: '250px', fontSize: '0.75rem', lineHeight: 1.5 } } }}>
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </Tooltip>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="center" mt={2}>
          <Select value={year} onChange={handleYearChange}>
            {[2024, 2025, 2026].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </MDBox>
        <MDBox height="100%">
          <Scatter data={data} options={chartOptions} />
        </MDBox>
      </MDBox>
    </Card>
  );
};

RRRatioAnalysisChart.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string,
};

export default RRRatioAnalysisChart;